/**
 * DINING CHAIRS BY COUNT (2026-10-06, Matt: "For the dining table, allow the user to add
 * dining chairs by count as an instance parameter. Once they are set by the user, allow
 * them to be moved and customized. If the user attempts to change the count after any
 * customization, warn them that custom changes will be reset if they continue.").
 *
 * The table's `chairCount` instance param GENERATES real dining-chair objects around it —
 * ordinary elements the user can select, move, rotate, retype, restyle or delete. Each
 * chair carries its link back (`chairOfTableId`) and the pose + settings it was generated
 * with (`chairGenLocal` = [x, y, turn°] in the TABLE's frame, `chairGenTypeId`,
 * `chairGenSig`), which is how a customization is told apart from a chair simply riding
 * its table:
 *  - the table carries its chairs: translate / rotate / mirror / copy / delete of the
 *    table apply to its chairs too (geometryOps + elements.ts), so a moved table never
 *    reads as customized — the chairs' table-relative pose is unchanged;
 *  - a chair moved off its spot, turned, retyped, given any setting of its own, or
 *    deleted (fewer chairs than the count) IS a customization (`diningChairsCustomized`);
 *  - changing the count regenerates them all (`setDiningChairCount`, routed from
 *    applyParamEdit so every edit path agrees); the palette asks first when any chair
 *    is customized — the warning lives in the UI, the reset here.
 * Chairs are laid out the way a table is set: both long sides first, a chair at the head
 * when the count is odd, both ends once there are six or more; pushed in so the seat's
 * front tucks 6" under the table edge.
 */
import type { ElementId, ParamValue, Pt2, TypeId } from '../types'
import { newElementId, newNodeId } from '../ids'
import { getElement, getType, listElements, listTypes, maps, recordToYMap, resolveElement, transact, type ModelDoc } from '../doc'

export const DINING_TABLE_KIND = 'diningTable'
export const DINING_CHAIR_KIND = 'diningChair'
export const DINING_CHAIR_MAX = 16
/** how far the seat front tucks under the table edge */
const TUCK_FT = 0.5

/** a generated chair's link to its table + what it was generated with */
export const CHAIR_LINK_KEYS = ['chairOfTableId', 'chairGenLocal', 'chairGenSig', 'chairGenTypeId'] as const

const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)

export function isDiningTable(model: ModelDoc, id: ElementId): boolean {
  const rec = getElement(model, id)
  return !!rec && rec.category === 'object' && getType(model, rec.typeId)?.params?.objectKind === DINING_TABLE_KIND
}

/** the chairs generated for a table (linked by `chairOfTableId`) */
export function chairsOfTable(model: ModelDoc, tableId: ElementId): ElementId[] {
  return listElements(model)
    .filter((el) => el.category === 'object' && el.params?.chairOfTableId === tableId)
    .map((el) => el.id)
}

/** drop chairs whose table is in the same set — the table carries them (a move or copy
 *  of both would otherwise move / copy each chair twice) */
export function dropCarriedChairs(model: ModelDoc, ids: ElementId[]): ElementId[] {
  const set = new Set(ids)
  return ids.filter((id) => {
    const t = getElement(model, id)?.params?.chairOfTableId
    return !(typeof t === 'string' && set.has(t as ElementId))
  })
}

/** an anchored object's frame: origin, across (u), forward (v), rotation° */
function frameOf(params: Record<string, unknown> | undefined): { o: Pt2; u: Pt2; v: Pt2; rot: number } | null {
  const o = (params?.path as { nodes?: { pt: Pt2 }[] } | undefined)?.nodes?.[0]?.pt
  if (!o) return null
  const rot = num(params?.rotationDeg, 0)
  const r = (rot * Math.PI) / 180
  return { o: [o[0], o[1]], u: [Math.cos(r), Math.sin(r)], v: [-Math.sin(r), Math.cos(r)], rot }
}

/**
 * The chair spots around a w × d table, in the TABLE's local frame (x across the width
 * centred on 0, y from the anchor 0 to d): each chair's anchor (the back of its seat)
 * and its turn relative to the table, facing in.
 */
export function diningChairLayout(w: number, d: number, n: number, chairDepth: number): { x: number; y: number; turn: number }[] {
  n = Math.max(0, Math.min(DINING_CHAIR_MAX, Math.floor(n)))
  if (!n) return []
  const ends = n <= 2 ? 0 : n % 2 === 1 ? 1 : n >= 6 ? 2 : 0
  const sides = n - ends
  const sideA = Math.ceil(sides / 2), sideB = sides - sideA
  const back = Math.max(0, chairDepth - TUCK_FT)
  const out: { x: number; y: number; turn: number }[] = []
  // a chair whose seat faces `inward` (unit, table frame) with its front at edge point e
  const put = (e: Pt2, inward: Pt2) => {
    out.push({ x: e[0] - inward[0] * back, y: e[1] - inward[1] * back, turn: (Math.atan2(-inward[0], inward[1]) * 180) / Math.PI })
  }
  const cy = d / 2
  const alongX = w >= d   // the long sides run across the width
  const len = alongX ? w : d
  const row = (k: number, side: 1 | -1) => {
    for (let i = 0; i < k; i++) {
      const t = -len / 2 + (len * (i + 0.5)) / k
      if (alongX) put([t, side > 0 ? d : 0], [0, -side])
      else put([side > 0 ? w / 2 : -w / 2, cy + t], [-side, 0])
    }
  }
  row(sideA, 1)
  row(sideB, -1)
  // the head (and the foot once there are six or more)
  const endAt = (side: 1 | -1) => (alongX ? put([side * (w / 2), cy], [-side, 0]) : put([0, side > 0 ? d : 0], [0, -side]))
  if (ends >= 1) endAt(1)
  if (ends >= 2) endAt(-1)
  return out
}

/** the signature of a chair's own settings — everything but its pose and its links */
function chairSig(params: Record<string, unknown>): string {
  const skip = new Set(['path', 'rotationDeg', 'pinned', 'chairOfTableId', 'chairGenLocal', 'chairGenSig', 'chairGenTypeId'])
  const keys = Object.keys(params).filter((k) => !skip.has(k)).sort()
  return JSON.stringify(keys.map((k) => [k, params[k]]))
}

/** the chair type to set: the first type whose kind is a dining chair */
function chairType(model: ModelDoc): { id: TypeId; depthFt: number } | null {
  const t = listTypes(model).find((x) => x.category === 'object' && x.params?.objectKind === DINING_CHAIR_KIND)
  return t ? { id: t.id, depthFt: num(t.params.depthFt, 1.833333) } : null
}

/**
 * Set a table's chair count: the count, and a fresh set of chairs in their spots —
 * every existing chair of this table is removed first (that IS the reset the palette
 * warns about). One transaction, one undo step.
 */
export function setDiningChairCount(model: ModelDoc, tableId: ElementId, count: number): boolean {
  const em = maps(model).elements.get(tableId)
  const table = resolveElement(model, tableId)
  if (!em || !table) return false
  const n = Math.max(0, Math.min(DINING_CHAIR_MAX, Math.floor(num(count, 0))))
  const f = frameOf(table.params as Record<string, unknown>)
  const ct = chairType(model)
  const w = num(table.typeParams.widthFt, 6), d = num(table.typeParams.depthFt, 3.333333)
  // a chair stands on the floor / terrain its table stands on
  const rec = getElement(model, tableId)
  const host = rec?.hostId ? getElement(model, rec.hostId) : null
  const hostId = host && (host.category === 'floor' || host.category === 'topo') ? host.id : null
  transact(model, () => {
    ;(em.get('params') as { set: (k: string, v: unknown) => void }).set('chairCount', n)
    for (const id of chairsOfTable(model, tableId)) maps(model).elements.delete(id)
    if (!f || !ct) return
    for (const s of diningChairLayout(w, d, n, ct.depthFt)) {
      const pt: Pt2 = [f.o[0] + f.u[0] * s.x + f.v[0] * s.y, f.o[1] + f.u[1] * s.x + f.v[1] * s.y]
      const params: Record<string, ParamValue> = {
        path: { closed: false, nodes: [{ id: newNodeId(), pt }] } as unknown as ParamValue,
        rotationDeg: f.rot + s.turn,
        chairOfTableId: tableId,
        chairGenLocal: [s.x, s.y, s.turn] as unknown as ParamValue,
        chairGenTypeId: ct.id,
      }
      params.chairGenSig = chairSig(params)
      const id = newElementId()
      maps(model).elements.set(id, recordToYMap({
        id, category: 'object', typeId: ct.id, levelId: table.levelId,
        ...(hostId ? { hostId } : {}),
        ...(rec?.phaseCreated ? { phaseCreated: rec.phaseCreated } : {}),
        ...(rec?.phaseDemolished ? { phaseDemolished: rec.phaseDemolished } : {}),
        params,
      }))
    }
  })
  return true
}

const angDiff = (a: number, b: number) => Math.abs(((((a - b) % 360) + 540) % 360) - 180)

/**
 * True once the user has changed anything about this table's chairs: one moved off its
 * spot or turned, retyped, given a setting of its own, or deleted (fewer chairs than the
 * count). A chair carried along by its table is NOT a change.
 */
export function diningChairsCustomized(model: ModelDoc, tableId: ElementId): boolean {
  const table = getElement(model, tableId)
  if (!table) return false
  const count = Math.floor(num(table.params?.chairCount, 0))
  const chairs = chairsOfTable(model, tableId)
  if (chairs.length !== count) return true
  const f = frameOf(table.params as Record<string, unknown>)
  if (!f) return false
  // every chair must sit on one of the generated spots, one chair per spot — matched as
  // a SET, since mirroring a table carries its chairs onto each other's spots
  const spots: number[][] = []
  const here: [number, number, number][] = []
  for (const id of chairs) {
    const c = getElement(model, id)
    if (!c) return true
    const p = c.params as Record<string, unknown>
    const gen = p.chairGenLocal as number[] | undefined
    const cf = frameOf(p)
    if (!gen || !cf) return true
    if (c.typeId !== p.chairGenTypeId) return true
    if (chairSig(p) !== p.chairGenSig) return true
    spots.push(gen)
    const dx = cf.o[0] - f.o[0], dy = cf.o[1] - f.o[1]
    here.push([dx * f.u[0] + dy * f.u[1], dx * f.v[0] + dy * f.v[1], cf.rot - f.rot])
  }
  const used = new Set<number>()
  for (const h of here) {
    const k = spots.findIndex((g, i) => !used.has(i) && Math.hypot(h[0] - g[0], h[1] - g[1]) <= 0.02 && angDiff(h[2], g[2]) <= 0.5)
    if (k < 0) return true
    used.add(k)
  }
  return false
}
